import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import type { Employee } from '../../../store/slices/employeeSlice';
import { DefinitionList, PersonAvatar, PersonCell } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { formatNumber, MISSING } from '../../../utils/format';
import { getEmployeeTeamLabel, SaveErrorCopy, UNASSIGNED_TEAM } from './orgChartModel';

export interface PlacementDialogProps {
  employee: Employee | null;
  managerOptions: Employee[];
  managerId: string;
  managerName: string;
  teamName: string;
  teamOptions: string[];
  directReportCount: number;
  employeeById: Map<string, Employee>;
  isSaving: boolean;
  /** The last save's failure, shown inside the dialog so it's read in the modal's context. */
  error?: SaveErrorCopy | null;
  onErrorAction?: (to: string) => void;
  onManagerChange: (managerId: string, managerName: string) => void;
  onManagerNameChange: (value: string) => void;
  onTeamChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
}

/**
 * Edit one person's placement: who they report to and which team they sit
 * in. Searchable pickers because both lists run long (book §11.4).
 */
const PlacementDialog: React.FC<PlacementDialogProps> = ({
  employee,
  managerOptions,
  managerId,
  managerName,
  teamName,
  teamOptions,
  directReportCount,
  employeeById,
  isSaving,
  error,
  onErrorAction,
  onManagerChange,
  onManagerNameChange,
  onTeamChange,
  onClose,
  onSave,
}) => {
  const selectedManager = managerId ? employeeById.get(managerId) ?? null : null;
  const departmentName = employee?.department?.name;
  const currentTeam = employee ? getEmployeeTeamLabel(employee) : UNASSIGNED_TEAM;

  return (
    <Dialog open={Boolean(employee)} onClose={isSaving ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="org-placement-title">
      <DialogTitle id="org-placement-title">Edit placement</DialogTitle>
      <DialogContent>
        {employee && (
          <Box sx={{ display: 'grid', gap: 2.5, pt: 0.5 }}>
            <PersonCell name={employee.name} secondary={employee.email} size={40} />

            {error && (
              <Alert
                severity="error"
                action={
                  error.action && onErrorAction ? (
                    <Button variant="text" size="small" onClick={() => onErrorAction(error.action!.to)}>
                      {error.action.label}
                    </Button>
                  ) : undefined
                }
              >
                {error.message}
              </Alert>
            )}

            <Autocomplete<Employee, false, false, false>
              options={managerOptions}
              value={selectedManager}
              onChange={(_, value) => onManagerChange(value?._id || '', value?.name || '')}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: string };
                const team = option.teamName || option.department?.name || '';
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <PersonAvatar name={option.name} size={24} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body1" component="div" noWrap sx={{ color: sbg.text.primary }}>
                        {option.name}
                      </Typography>
                      {team && (
                        <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                          {team}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reports to"
                  placeholder="No manager (top level)"
                  helperText="Leave empty to place them at the top of the chart."
                />
              )}
            />

            <TextField
              label="Manager name (not in the directory)"
              value={managerName}
              onChange={(event) => onManagerNameChange(event.target.value)}
              disabled={Boolean(managerId)}
              placeholder="e.g. Maria Chen"
              helperText={
                managerId
                  ? 'Clear “Reports to” to type a name instead.'
                  : "Use this when their manager doesn't have an employee record yet."
              }
            />

            <Autocomplete<string, false, false, false>
              options={teamOptions}
              value={teamName || null}
              onChange={(_, value) => onTeamChange(value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Team"
                  placeholder={departmentName ? `Department default (${departmentName})` : 'Department default'}
                  helperText="Leave empty to use their department as the team."
                />
              )}
            />

            <Box sx={{ bgcolor: sbg.bg.surfaceSubtle, borderRadius: sbg.radius.lg, px: 2, pt: 1.5, pb: 0.5 }}>
              <Typography variant="overline" component="h3" sx={{ color: sbg.text.secondary }}>
                Current placement
              </Typography>
              <DefinitionList
                sx={{ '& > div:last-of-type': { borderBottom: 0 } }}
                items={[
                  { label: 'Manager', value: employee.managerName || MISSING },
                  { label: 'Team', value: currentTeam },
                  { label: 'Direct reports', value: formatNumber(directReportCount) },
                ]}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave} disabled={isSaving} aria-busy={isSaving || undefined}>
          {isSaving ? 'Saving…' : 'Save placement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlacementDialog;
